import { EyeOutlined, MoreOutlined } from '@ant-design/icons'
import type { ColumnDef } from '@tanstack/react-table'
import type { MenuProps } from 'antd'
import { Button, Dropdown, Tag } from 'antd'
import classNames from 'classnames'

import { PartnershipModel, PartnershipStatus } from '@/types'

import cn from './PartnershipsTab.module.scss'

export const PARTNERSHIPS_COLUMN_WIDTHS: Partial<Record<string, string>> = {
  partner: 'minmax(200px, 1.4fr)',
  status: '130px',
  initiatedBy: 'minmax(160px, 1fr)',
  mySharing: '150px',
  partnerSharing: '200px',
  eta: '180px',
  createdAt: '120px',
  actions: '64px',
}

const STATUS_CONFIG: Record<
  PartnershipStatus,
  { label: string; color: string }
> = {
  PROPOSED: { label: 'Ожидает', color: 'blue' },
  ACTIVE: { label: 'Активно', color: 'green' },
  TERMINATED: { label: 'Расторгнуто', color: 'default' },
  REJECTED: { label: 'Отклонено', color: 'red' },
}

export const getTanstackColumns = (params: {
  myOrganisationName: string
  isOrganisationSelected: boolean
  onViewDetails: (p: PartnershipModel) => void
  onAccept: (p: PartnershipModel) => void
  onReject: (p: PartnershipModel) => void
  onTerminate: (p: PartnershipModel) => void
  onSettings: (p: PartnershipModel) => void
  onRevoke: (p: PartnershipModel) => void
  onRenew: (p: PartnershipModel) => void
}): ColumnDef<PartnershipModel, unknown>[] => {
  const {
    myOrganisationName,
    isOrganisationSelected,
    onViewDetails,
    onAccept,
    onReject,
    onTerminate,
    onSettings,
    onRevoke,
    onRenew,
  } = params

  return [
    {
      id: 'partner',
      header: 'Партнёр',
      cell: (info) => (
        <span className={cn.partnerName}>
          {info.row.original.partner.description}
        </span>
      ),
    },

    {
      id: 'status',
      header: 'Статус',
      cell: (info) => {
        const cfg = STATUS_CONFIG[info.row.original.status]
        return <Tag color={cfg.color}>{cfg.label}</Tag>
      },
    },

    {
      id: 'initiatedBy',
      header: 'Инициатор',
      cell: (info) => {
        const record = info.row.original
        return record.initiatedByMe ? (
          <span
            className={classNames(
              cn.badgeMine,
              isOrganisationSelected && cn.badgeMineSelected
            )}
          >
            {myOrganisationName || 'Я'}
          </span>
        ) : (
          <span className={cn.badgePartner}>{record.partner.description}</span>
        )
      },
    },

    {
      id: 'mySharing',
      header: 'Передаю остатки',
      cell: (info) => {
        const sharesData = info.row.original.mySettings?.sharesData
        return sharesData ? <Tag color="green">Да</Tag> : <Tag>Нет</Tag>
      },
    },

    {
      id: 'partnerSharing',
      header: 'Партнёр передаёт остатки',
      cell: (info) => {
        const sharesData = info.row.original.partnerSettings?.sharesData
        return sharesData ? <Tag color="green">Да</Tag> : <Tag>Нет</Tag>
      },
    },

    {
      id: 'eta',
      header: 'Срок доставки (дней)',
      cell: (info) => {
        const eta = info.row.original.mySettings?.etaDays ?? 0
        return eta > 0 ? (
          <span>{eta}</span>
        ) : (
          <span className={cn.dimText}>—</span>
        )
      },
    },

    {
      id: 'createdAt',
      header: 'Создано',
      cell: (info) => {
        const d = new Date(info.row.original.createdAt)
        return (
          <span className={cn.dimText}>{d.toLocaleDateString('ru-RU')}</span>
        )
      },
    },

    {
      id: 'actions',
      header: '',
      cell: (info) => {
        const record = info.row.original
        const items: MenuProps['items'] = []

        if (record.status === 'ACTIVE') {
          items.push({
            key: 'details',
            label: 'Подробнее',
            icon: <EyeOutlined />,
            onClick: () => onViewDetails(record),
          })
        }

        if (record.status === 'PROPOSED' && !record.initiatedByMe) {
          items.push({
            key: 'accept',
            label: 'Принять',
            onClick: () => onAccept(record),
          })
          items.push({
            key: 'reject',
            label: 'Отклонить',
            danger: true,
            onClick: () => onReject(record),
          })
        }

        if (record.status === 'PROPOSED' && record.initiatedByMe) {
          items.push({
            key: 'revoke',
            label: 'Отозвать',
            danger: true,
            onClick: () => onRevoke(record),
          })
        }

        if (record.status === 'TERMINATED' || record.status === 'REJECTED') {
          items.push({
            key: 'renew',
            label:
              record.status === 'TERMINATED'
                ? 'Возобновить партнёрство'
                : 'Повторить запрос',
            onClick: () => onRenew(record),
          })
        }

        if (record.status === 'ACTIVE') {
          items.push({
            key: 'settings',
            label: 'Настройки',
            onClick: () => onSettings(record),
          })
          items.push({
            key: 'terminate',
            label: 'Расторгнуть',
            danger: true,
            onClick: () => onTerminate(record),
          })
        }

        return items.length > 0 ? (
          <Dropdown
            menu={{ items }}
            trigger={['hover']}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        ) : null
      },
    },
  ]
}
